/**
 * Authoritative permission vocabulary, shared by server and browser.
 * Persisted custom roles retain lenient expansion in the engine.
 *
 * Resource and action order determines passport bitset indices. Append new
 * entries; never reorder or remove them. Tests pin the stable indices.
 */
import type { ScopeTier } from "./vocabulary";

export const AUTHZ_ACTIONS = [
  "view",
  "create",
  "update",
  "delete",
  "manage",
  "share",
  "rotate",
  "attach",
  "detach",
  "viewOtherPersonal",
] as const;

export type AuthzAction = (typeof AUTHZ_ACTIONS)[number];

const READ_ONLY = ["view"] as const;

export const AUTHZ_RESOURCES = {
  organization: {
    actions: ["view", "manage", "delete"],
    scopes: ["organization"],
  },
  project: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  team: {
    actions: ["view", "manage"],
    scopes: ["project", "team", "organization"],
  },
  analytics: {
    // `analytics:create` is requested by the dashboards router even though no
    // bag grants it directly (manage implies it via the hierarchy).
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  cost: {
    actions: READ_ONLY,
    scopes: ["project", "team", "organization"],
  },
  traces: {
    // No `traces:manage` exists anywhere in the legacy vocabulary — traces
    // deliberately have no blanket-manage, so no hierarchy family here.
    actions: ["view", "create", "update", "share"],
    scopes: ["project", "team", "organization"],
  },
  scenarios: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  annotations: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  evaluations: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  datasets: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  triggers: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  workflows: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  experiments: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  prompts: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  secrets: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  playground: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  ops: {
    // Platform-tier only: grantable by exactly one source (ADMIN_EMAILS →
    // platform-ops principal), never by org/team/project bindings.
    actions: ["view", "manage"],
    scopes: ["platform"],
  },
  auditLog: {
    actions: READ_ONLY,
    scopes: ["project", "team", "organization"],
  },
  virtualKeys: {
    actions: [
      "view",
      "create",
      "update",
      "delete",
      "rotate",
      "manage",
      "viewOtherPersonal",
    ],
    scopes: ["project", "team", "organization"],
  },
  gatewayBudgets: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  gatewayProviders: {
    actions: ["view", "update", "manage"],
    scopes: ["project", "team", "organization"],
  },
  routingPolicies: {
    actions: ["view", "manage"],
    scopes: ["project", "team", "organization"],
  },
  gatewayGuardrails: {
    actions: ["view", "attach", "detach", "manage"],
    scopes: ["project", "team", "organization"],
  },
  gatewayLogs: {
    // Deprecated in the legacy vocabulary but still granted by every bag;
    // kept for parity until the stage-E breaking-change pass drops it.
    actions: READ_ONLY,
    scopes: ["project", "team", "organization"],
  },
  gatewayUsage: {
    actions: READ_ONLY,
    scopes: ["project", "team", "organization"],
  },
  gatewayCacheRules: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  governance: {
    actions: ["view", "manage"],
    scopes: ["organization"],
  },
  ingestionSources: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["organization"],
  },
  anomalyRules: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["organization"],
  },
  complianceExport: {
    actions: READ_ONLY,
    scopes: ["organization"],
  },
  activityMonitor: {
    actions: READ_ONLY,
    scopes: ["organization"],
  },
  aiTools: {
    actions: ["view", "manage"],
    scopes: ["organization"],
  },
  webhookEndpoints: {
    // Org-tier only: endpoints carry signing secrets and stream event
    // families out of the platform (see the legacy vocabulary's rationale).
    actions: ["view", "manage"],
    scopes: ["organization"],
  },
  gatewaySpend: {
    actions: ["view", "manage"],
    scopes: ["organization"],
  },
  langy: {
    actions: ["view", "create", "update", "delete", "manage"],
    scopes: ["project", "team", "organization"],
  },
  agentCache: {
    // The per-project cache an agent writes its own run state into. `view`
    // reads an entry, `manage` writes and deletes one.
    actions: ["view", "manage"],
    scopes: ["project", "team", "organization"],
  },
  governanceCost: {
    // The organization's cost screen (ADR-128): what the provider billed,
    // what the gateway metered, and the seat lane, side by side.
    //
    // `view` only. Nothing on the screen is editable — the figures are
    // summarized from the cost rollup, so there is no write grain to grant,
    // and a `manage` nobody can act on would still widen `view` through the
    // hierarchy rule for anyone holding it.
    //
    // Org-tier only: the screen aggregates every lane of the organization's
    // spend across every team, so a team- or project-scoped binding must
    // never grant it.
    actions: ["view"],
    scopes: ["organization"],
  },
  // D05. Seeing a connection and changing one are two permissions, because
  // they are two jobs: a security reviewer reads which domains route and who
  // proved them; an IT administrator sets the thing up. Org-tier only, like
  // `governance` and `webhookEndpoints` above — a connection decides how
  // EVERYONE in the organization signs in, so a team- or project-scoped
  // grant of it would be a grant whose blast radius is the whole
  // organization while its label says otherwise.
  //
  // The directory that provisions people into the organization is gated by
  // these same two: federating sign-in and letting a directory write your
  // membership are set up by the same administrator, out of the same
  // connection, and a second pair would have been a second name for one job.
  sso: {
    actions: ["view", "manage"],
    scopes: ["organization"],
  },
} as const satisfies Record<
  string,
  {
    actions: readonly string[];
    scopes: readonly ("project" | "team" | "organization" | "platform")[];
  }
>;

export type AuthzResource = keyof typeof AUTHZ_RESOURCES;

/** The tiers a permission may be granted at. Every scope tier except
 *  `resource`, which is reached by a grant on the resource itself rather
 *  than by a permission declaration. */
export type AuthzScopeType = Exclude<ScopeTier, "resource">;

/** Only VALID resource:action pairs — `traces:rotate` is a type error. */
export type AuthzPermission = {
  [
    R in AuthzResource
  ]: `${R}:${(typeof AUTHZ_RESOURCES)[R]["actions"][number]}`;
}[AuthzResource];

/**
 * Stable, append-only enumeration of every valid permission. Declaration
 * order IS the bitset index (stage F passports depend on indices never
 * moving), enforced by sentinel tests.
 */
export const ALL_PERMISSIONS: readonly AuthzPermission[] = Object.entries(
  AUTHZ_RESOURCES,
).flatMap(([resource, def]) =>
  def.actions.map((action) => `${resource}:${action}` as AuthzPermission),
);

const PERMISSION_INDEX: ReadonlyMap<string, number> = new Map(
  ALL_PERMISSIONS.map((permission, index) => [permission, index]),
);

export function isRegistryPermission(value: string): value is AuthzPermission {
  return PERMISSION_INDEX.has(value);
}

/** Bitset index for a permission, or undefined for unknown strings. */
export function permissionIndex(permission: string): number | undefined {
  return PERMISSION_INDEX.get(permission);
}

export function permissionResource(permission: string): string {
  return permission.split(":")[0] ?? "";
}

/**
 * Permission hierarchy: `<resource>:manage` satisfies
 * view/create/update/delete/rotate/attach/detach on the same resource.
 */
const MANAGE_IMPLIED_ACTIONS: ReadonlySet<string> = new Set([
  "view",
  "create",
  "update",
  "delete",
  "rotate",
  "attach",
  "detach",
]);

/**
 * True when `requested` is satisfied by the `granted` set, applying the
 * manage-implication rule. `granted` may contain non-registry strings
 * (legacy custom-role rows) — membership is string-based on purpose.
 */
export function permissionSatisfiedBy({
  granted,
  requested,
}: {
  granted: ReadonlySet<string>;
  requested: string;
}): boolean {
  if (granted.has(requested)) return true;
  const separator = requested.lastIndexOf(":");
  if (separator === -1) return false;
  const action = requested.slice(separator + 1);
  if (!MANAGE_IMPLIED_ACTIONS.has(action)) return false;
  return granted.has(`${requested.slice(0, separator)}:manage`);
}

/**
 * ADR-092 §8 — the resource tier: the individually shareable resource kinds.
 *
 * `children` is documentation, not behaviour. Nothing reads it: a child read
 * authorizes at its parent resource's node because the CALLER supplies that
 * ancestry as `scope.parents`, and the walk consults only that. The lists are
 * here so the resource knowledge has one home while stage C5 turns ShareLink
 * into full resource-grant storage.
 */
export const SHAREABLE_RESOURCE_KINDS = {
  trace: {
    children: ["span", "log", "metric", "evaluation", "annotation"],
  },
  thread: {
    children: ["trace"],
  },
} as const satisfies Record<string, { children: readonly string[] }>;

export type ShareableResourceKind = keyof typeof SHAREABLE_RESOURCE_KINDS;

/**
 * ADR-021 scope fence as registry data: a binding at `scopeType` may grant
 * `permission` only when the permission's resource is grantable at or below
 * that tier. Org-exclusive resources (scopes: ["organization"]) need an
 * ORGANIZATION-scoped binding; platform resources are never grantable by
 * org/team/project bindings at all.
 */
export function bindingScopeCanGrantPermission({
  scopeType,
  permission,
}: {
  scopeType: "ORGANIZATION" | "TEAM" | "PROJECT";
  permission: string;
}): boolean {
  const resource = permissionResource(permission);
  const def = AUTHZ_RESOURCES[resource as AuthzResource];
  // Unknown resources (legacy custom-role strings outside the registry) are
  // treated as non-exclusive, matching the legacy fence which only checks a
  // fixed org-exclusive set.
  if (!def) return true;
  const scopes: readonly AuthzScopeType[] = def.scopes;
  // LEGACY-QUIRK(C): the legacy fence only knows the org-exclusive set, so a
  // custom role CAN today grant `ops:*` from any binding. The platform tier
  // becomes a real fence in stage C when platform-ops turns into a principal.
  if (scopes.includes("platform")) return true;
  if (scopeType === "ORGANIZATION") return true;
  return scopes.includes("team") || scopes.includes("project");
}
